/**
 * WorkspaceSettingsPage - Page de configuration workspace avec onglets.
 * 
 * Features:
 * - Onglets : Branding, Domaines, Déploiement
 * - Navigation par URL (/workspace/:id/settings/:tab)
 * - Responsive et accessible
 * 
 * @usage /workspace/1/settings/branding
 */
import React, { useState, useMemo, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Palette, Globe, Rocket, Brain } from 'lucide-react'
import WorkspaceBrandingPage from './WorkspaceBrandingPage'
import WorkspaceDomainsPage from './WorkspaceDomainsPage'
import WorkspaceDeploymentPage from './WorkspaceDeploymentPage'
import WorkspaceLlmCatalogPanel from './WorkspaceLlmCatalogPanel'
import { useAppLanguage } from '../i18n'

type TabType = 'branding' | 'domains' | 'deployment' | 'llm-catalog'

export default function WorkspaceSettingsPage() {
  const { workspaceId, tab } = useParams<{ workspaceId: string; tab?: string }>()
  const navigate = useNavigate()
  const { t } = useAppLanguage()
  const lc = t.agents.llmCatalog

  const TABS: { id: TabType; label: string; icon: React.ReactNode; description: string }[] = useMemo(
    () => [
      {
        id: 'branding',
        label: 'Branding',
        icon: <Palette className="h-5 w-5" />,
        description: 'Logo, couleurs et watermark',
      },
      {
        id: 'domains',
        label: 'Domaines',
        icon: <Globe className="h-5 w-5" />,
        description: 'Domaines autorisés pour le widget',
      },
      {
        id: 'deployment',
        label: 'Déploiement',
        icon: <Rocket className="h-5 w-5" />,
        description: 'Mode Preview/Production',
      },
      {
        id: 'llm-catalog',
        label: lc.workspaceTab,
        icon: <Brain className="h-5 w-5" />,
        description: lc.workspaceTabDesc,
      },
    ],
    [lc.workspaceTab, lc.workspaceTabDesc]
  )

  const [activeTab, setActiveTab] = useState<TabType>(() => {
    const u = (tab as TabType) || 'branding'
    return u === 'llm-catalog' || u === 'branding' || u === 'domains' || u === 'deployment'
      ? u
      : 'branding'
  })

  useEffect(() => {
    const u = (tab as TabType) || 'branding'
    if (u === 'llm-catalog' || u === 'branding' || u === 'domains' || u === 'deployment') {
      setActiveTab(u)
    }
  }, [tab])

  const handleTabChange = (newTab: TabType) => {
    setActiveTab(newTab)
    navigate(`/workspace/${workspaceId}/settings/${newTab}`)
  }

  const renderContent = () => {
    switch (activeTab) {
      case 'branding':
        return <WorkspaceBrandingPage />
      case 'domains':
        return <WorkspaceDomainsPage />
      case 'deployment':
        return <WorkspaceDeploymentPage />
      case 'llm-catalog':
        return <WorkspaceLlmCatalogPanel />
      default:
        return <WorkspaceBrandingPage />
    }
  }

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950">
      {/* Header */}
      <div className="border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div>
            <h1 className="text-3xl font-bold text-zinc-900 dark:text-zinc-100">
              Paramètres du Workspace
            </h1>
            <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400">
              Configurez le branding, les domaines autorisés et le mode de déploiement
            </p>
          </div>

          {/* Tabs */}
          <div className="mt-6 flex gap-1 overflow-x-auto">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`
                  flex items-center gap-2 px-4 py-3 rounded-lg font-medium text-sm transition-all whitespace-nowrap
                  ${activeTab === tab.id
                    ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 shadow-sm'
                    : 'text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800'
                  }
                `}
              >
                {tab.icon}
                <div className="text-left">
                  <div className="font-medium">{tab.label}</div>
                  <div className="text-xs opacity-75">{tab.description}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto">
        {renderContent()}
      </div>
    </div>
  )
}
